import { useQuery } from '@tanstack/react-query'
import { useApi } from '../store/auth.store'
import type { DashboardSummary, DashboardKPIs } from '../types'

const DASHBOARD_REFETCH_MS = 30_000

export type DateRange = '7d' | '30d' | '90d' | 'all'

/**
 * Hook unificado del Dashboard. Reemplaza 5 hooks separados.
 * 1 sola request a /dashboard/overview con TODO lo necesario.
 */
export function useDashboardOverview(range: DateRange = '30d', mailboxId?: string) {
  const api = useApi()
  const mbParam = mailboxId ? `&mailboxId=${mailboxId}` : ''
  return useQuery<DashboardOverview>({
    queryKey: ['dashboard', 'overview', range, mailboxId],
    queryFn: () => api.get<DashboardOverview>(`/dashboard/overview?range=${range}${mbParam}`),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  })
}

// Tipo del overview (debe coincidir con el backend getOverview)
export interface DashboardOverview {
  kpis: {
    openCases: number
    activeAlerts: number
    criticalAlerts: number
    unprocessedEmails: number
    avgResolutionHours: number | null
  }
  period: {
    start: string
    end: string
    totalCases: number
    resolvedCases: number
    resolutionRate: number
    escalationRate: number
  }
  slaCompliance: {
    totalOpen: number
    onTrack: number
    atRisk: number
    breached: number
    rate: number
  }
  casesByStatus: Array<{
    id: string
    name: string
    label: string
    color: string
    isFinal: boolean
    count: number
  }>
  alertsBySeverity: Array<{
    id: string
    name: string
    label: string
    color: string
    count: number
  }>
  alertsByType: Array<{
    id: string
    name: string
    label: string
    count: number
  }>
  recentAlerts: Array<{
    id: string
    title: string
    status: string
    createdAt: string
    severity: { name: string; label: string; color: string } | null
    type: { name: string; label: string } | null
  }>
  emailStats: {
    total: number
    processed: number
    withCases: number
    unread: number
    withAlerts: number
    byDay: Array<{ date: string; count: number }>
  }
  casesByCriticidad: Array<{
    label: string
    count: number
    href: string
  }>
  casesByMailbox: Array<{
    mailbox: string
    count: number
  }>
  myQueue: {
    openCases: number
    dueSoon: number
    href: string
  }
  generatedAt: string
}

// ── Hooks antiguos (mantener por compat) ──

export function useDashboardSummary(mailboxId?: string) {
  const api = useApi()
  const params = mailboxId ? `?mailboxId=${mailboxId}` : ''
  return useQuery<DashboardSummary>({
    queryKey: ['dashboard', 'summary', mailboxId],
    queryFn: () => api.get<DashboardSummary>(`/dashboard/summary${params}`),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}

export function useDashboardKPIs(dateRange?: { start: string; end: string }) {
  const api = useApi()
  const params = dateRange ? `?start=${dateRange.start}&end=${dateRange.end}` : ''
  return useQuery<DashboardKPIs>({
    queryKey: ['dashboard', 'kpis', dateRange],
    queryFn: () => api.get<DashboardKPIs>(`/dashboard/kpis${params}`),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}

export function useCasesByStatus() {
  const api = useApi()
  return useQuery({
    queryKey: ['dashboard', 'cases-by-status'],
    queryFn: () => api.get<{ status: string; count: number }[]>('/dashboard/cases/by-status'),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}

export function useSLACompliance() {
  const api = useApi()
  return useQuery({
    queryKey: ['dashboard', 'sla-compliance'],
    queryFn: () => api.get('/dashboard/sla/compliance'),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}

export function useAlertsSummary() {
  const api = useApi()
  return useQuery<any>({
    queryKey: ['dashboard', 'alerts-summary'],
    queryFn: () => api.get('/dashboard/alerts/summary'),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}

export function useEmailStats() {
  const api = useApi()
  return useQuery<any>({
    queryKey: ['dashboard', 'email-stats'],
    queryFn: () => api.get('/emails/stats'),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchIntervalInBackground: false,
  })
}
